import {
  DrawerBodyProps,
  DrawerCloseTriggerProps,
  DrawerHeaderProps,
  DrawerRootProps,
  Icon,
  Portal,
} from '@chakra-ui/react';
import { FC, ReactNode, useEffect, useRef } from 'react';
import { MdClose } from 'react-icons/md';
import {
  MBox,
  MButton,
  MDivider,
  MDrawer,
  MDrawerBody,
  MDrawerCloseButton,
  MDrawerContent,
  MDrawerFooter,
  MDrawerHeader,
  MDrawerOverlay,
  MDrawerPositioner,
  MFlex,
} from '../Monetize';

interface MDrawerContainerProps {
  isOpen: boolean;
  refToFocusOnClose?: React.RefObject<HTMLElement | null>;
  header: ReactNode;
  drawerBtnProps?: Partial<DrawerRootProps>;
  drawerCloseBtnProps?: Partial<DrawerCloseTriggerProps>;
  drawerHeaderProps?: Partial<DrawerHeaderProps>;
  drawerBodyProps?: Partial<DrawerBodyProps>;
  onClose: () => void;
  children: ReactNode;
}

/**
 * Drawer wrapper so that components don't have to be concerned with the structure
 *
 * v3 port: Drawer is built directly on Ark's Dialog machine (same as
 * Modal/Dialog), so the internal translation mirrors MCenterModal.tsx's:
 * `isOpen`/`onClose` → `open`/`onOpenChange`, `finalFocusRef` (a ref object)
 * → `finalFocusEl` (a function returning the element), `placement="right"`
 * → `"end"` (v3 placements are logical, not physical). `Drawer.CloseTrigger`
 * renders no default icon in v3, so one is supplied explicitly here, same
 * gap as Dialog/Menu/Popover.
 */
export const MDrawerContainer: FC<MDrawerContainerProps> = ({
  isOpen,
  refToFocusOnClose,
  header,
  drawerBtnProps,
  drawerCloseBtnProps,
  drawerHeaderProps,
  drawerBodyProps,
  onClose,
  children,
}) => {
  const drawerBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (drawerBodyRef.current?.contains(e.target as Node)) {
        e.stopPropagation();
      }
    };

    document.addEventListener('wheel', handleWheel, {
      capture: true,
      passive: false,
    });
    return () =>
      document.removeEventListener('wheel', handleWheel, { capture: true });
  }, [isOpen]);

  return (
    <MDrawer
      open={isOpen}
      placement="end"
      onOpenChange={(e) => {
        if (!e.open) {
          onClose();
        }
      }}
      finalFocusEl={
        refToFocusOnClose ? () => refToFocusOnClose.current : undefined
      }
      preventScroll={false}
      {...drawerBtnProps}
    >
      <Portal>
        <MDrawerOverlay />
        <MDrawerPositioner>
          <MDrawerContent>
            {/* This header is larger than most because it includes a button, requires minor adjustment */}
            <MDrawerCloseButton color="tPurple.base" {...drawerCloseBtnProps}>
              <Icon asChild>
                <MdClose />
              </Icon>
            </MDrawerCloseButton>
            <MDrawerHeader {...drawerHeaderProps}>{header}</MDrawerHeader>
            <MDrawerBody
              ref={drawerBodyRef}
              px="2"
              bgColor="tGray.sidebarDark"
              {...drawerBodyProps}
            >
              {children}
            </MDrawerBody>

            <MDivider />

            <MDrawerFooter>
              <MBox w="full">
                <MFlex justifyContent="center">
                  <MButton
                    mt={5}
                    variant="cancel"
                    justifySelf="center"
                    onClick={onClose}
                  >
                    Close
                  </MButton>
                </MFlex>
              </MBox>
            </MDrawerFooter>
          </MDrawerContent>
        </MDrawerPositioner>
      </Portal>
    </MDrawer>
  );
};
